import { USERCENTRICS_POSTHOG_SERVICE_NAME } from './config'

/**
 * `true`/`false`: Usercentrics answered. `null`: the CMP is not available (yet), so nothing may be
 * loaded and the caller has to wait for one of the CMP events.
 */
type PostHogConsentState = boolean | null

export interface PostHogConsentService {
  /** Usercentrics service id (template id), needed to write consent or open the details. */
  id: string
  given: boolean
  /** `true` while Usercentrics itself still asks the user (first layer open, no decision yet). */
  decisionPending: boolean
}

/**
 * Window events fired by the Usercentrics CMP. Any of them may change the answer, and re-reading
 * the consent is idempotent, so all of them trigger a re-evaluation.
 */
const USERCENTRICS_EVENTS = ['UC_UI_INITIALIZED', 'UC_CONSENT', 'UC_UI_CMP_EVENT'] as const

const normalizeServiceName = (name: string) => name.trim().toLowerCase()
const isPostHogService = (service: { name: string }) => normalizeServiceName(service.name) === normalizeServiceName(USERCENTRICS_POSTHOG_SERVICE_NAME)

/**
 * Whether Usercentrics itself still waits for the user's decision (first layer open). The legacy
 * `isConsentRequired()` is the documented signal and still exposed by the v3 loader; the v3
 * `consent.required` flag is the fallback.
 */
const isDecisionPending = (details?: UsercentricsConsentDetails): boolean => {
  const legacyUi = window.UC_UI
  if (typeof legacyUi?.isConsentRequired === 'function') {
    try {
      return legacyUi.isConsentRequired() === true
    } catch {
      // fall through to the v3 flag
    }
  }
  return details?.consent?.required === true
}

/**
 * Looks up the PostHog service in Usercentrics.
 *
 * - `null`: no CMP available (yet)
 * - `undefined`: CMP available, but the PostHog service is not configured in the console
 */
export const findPostHogService = async(): Promise<PostHogConsentService | null | undefined> => {
  const cmp = window.__ucCmp
  if (typeof cmp?.getConsentDetails === 'function') {
    try {
      const details = await cmp.getConsentDetails()
      const entry = Object.entries(details?.services ?? {}).find(([, service]) => isPostHogService(service))
      if (!entry) {
        return undefined
      }
      const [id, service] = entry
      return { id, given: service.consent?.given === true, decisionPending: isDecisionPending(details) }
    } catch {
      return null
    }
  }

  const legacyUi = window.UC_UI
  if (typeof legacyUi?.getServicesBaseInfo === 'function') {
    try {
      const service = legacyUi.getServicesBaseInfo().find(isPostHogService)
      if (!service) {
        return undefined
      }
      return { id: service.id, given: service.consent?.status === true, decisionPending: isDecisionPending() }
    } catch {
      return null
    }
  }

  return null
}

/**
 * Reads the consent for the PostHog service from Usercentrics.
 *
 * A configured CMP without a PostHog service yields `false`: the service has not been set up in
 * the Usercentrics console, so the user could not have agreed to anything.
 */
export const readPostHogConsent = async(): Promise<PostHogConsentState> => {
  const service = await findPostHogService()
  if (service === null) {
    return null
  }
  return service?.given === true
}

/**
 * Writes an explicit consent for the PostHog service into Usercentrics, so it is documented there
 * like a click in the Usercentrics dialog. Returns `false` when the CMP or the service is missing.
 */
export const grantPostHogConsent = async(): Promise<boolean> => {
  const service = await findPostHogService()
  if (!service) {
    return false
  }

  const cmp = window.__ucCmp
  if (typeof cmp?.updateServicesConsents === 'function') {
    await cmp.updateServicesConsents([{ id: service.id, consent: true }])
    await cmp.saveConsents?.('EXPLICIT')
    return true
  }

  const legacyUi = window.UC_UI
  if (typeof legacyUi?.acceptService === 'function') {
    legacyUi.acceptService(service.id)
    return true
  }

  return false
}

/** Opens the Usercentrics details of the PostHog service, falling back to the second layer. */
export const showPostHogConsentDetails = async(): Promise<void> => {
  const service = await findPostHogService()

  const cmp = window.__ucCmp
  if (cmp) {
    if (service && typeof cmp.showServiceDetails === 'function') {
      await cmp.showServiceDetails(service.id)
      return
    }
    await cmp.showSecondLayer?.()
    return
  }

  window.UC_UI?.showSecondLayer?.(service?.id)
}

export const subscribeToConsentChanges = (listener: () => void): (() => void) => {
  USERCENTRICS_EVENTS.forEach(eventName => window.addEventListener(eventName, listener))
  return () => {
    USERCENTRICS_EVENTS.forEach(eventName => window.removeEventListener(eventName, listener))
  }
}
